import { Typography } from '@iheartjane/reefer';
import type { ReactElement, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import type { ControllerRenderProps } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import type { FormFieldProps } from '../formField.types';

export interface RenderProps {
  children?: ReactNode;
  field: ControllerRenderProps;
  invalid?: boolean;
}

interface FormFieldWrapperProps extends FormFieldProps {
  /** The initial value of the field */
  defaultValue: unknown;

  /** Maximum date value to accept for this input */
  maxDate?: string;

  /** Maximum time value to accept for this input */
  maxTime?: string;

  /** Minimum date value to accept for this input */
  minDate?: string;

  /** Minimum time value to accept for this input */
  minTime?: string;

  /** Name of the field. Submitted with the form as part of a name/value pair */
  name: string;

  /** Render function */
  render: (renderProps: RenderProps) => ReactElement;

  /** Same as required validation, but allow value `false` to pass required validation */
  requiredAllowFalse?: boolean;
}

const ANNOUNCEMENT_DELAY_MS = 150;

/**
 * Defers error text updates so VoiceOver has time to finish processing focus
 * transitions before the live-region content changes. Without the delay,
 * VoiceOver swallows the announcement when it coincides with a focus move.
 *
 * The Typography is always in the DOM with `aria-live="assertive"`. When empty
 * it renders as a zero-height element (no margin, no content). Avoiding
 * clip/position:absolute keeps VoiceOver's mutation tracking reliable.
 */
function ErrorAnnouncement({
  name,
  show,
  message,
}: {
  message: string;
  name: string;
  show: boolean;
}) {
  const [announcedMessage, setAnnouncedMessage] = useState('');

  useEffect(() => {
    if (show && message) {
      setAnnouncedMessage('');
      const timer = setTimeout(() => {
        setAnnouncedMessage(message);
      }, ANNOUNCEMENT_DELAY_MS);
      return () => clearTimeout(timer);
    }
    setAnnouncedMessage('');
    return undefined;
  }, [show, message]);

  return (
    <Typography
      color="error"
      mt={announcedMessage ? 16 : 0}
      id={`${name}-error`}
      aria-live="assertive"
      aria-atomic="true"
    >
      {announcedMessage}
    </Typography>
  );
}

/**
 * This is a wrapper component for common functionality of form fields.
 */
export function FormFieldWrapper({
  defaultValue,
  errorMessage,
  errorOnBlur,
  max,
  maxDate,
  maxLength,
  maxTime,
  min,
  minDate,
  minLength,
  minTime,
  name,
  pattern,
  render,
  required = false,
  requiredAllowFalse = false,
  requiredMessage,
  shouldUnregister = true,
  validate,
}: FormFieldWrapperProps) {
  return (
    <Controller
      defaultValue={defaultValue}
      name={name}
      render={({ field, fieldState: { error, isTouched } }) => {
        const showError =
          error?.message && ((errorOnBlur && isTouched) || !errorOnBlur);
        return render({
          children: (
            <ErrorAnnouncement
              name={name}
              show={!!showError}
              message={error?.message ?? ''}
            />
          ),
          field,
          invalid: showError ? true : undefined,
        });
      }}
      rules={{
        max: max
          ? {
              message: `Please enter a numeric value less than or equal to ${max}.`,
              value: max,
            }
          : maxDate
            ? {
                message: `Please enter a date on or before ${maxDate}.`,
                value: maxDate,
              }
            : undefined,

        maxLength: maxLength && {
          message: `Please use no more than ${maxLength} characters.`,
          value: maxLength,
        },
        min: min
          ? {
              message: `Please enter a numeric value greater than or equal to ${min}.`,
              value: min,
            }
          : minDate
            ? {
                message: `Please enter a date on or after ${minDate}.`,
                value: minDate,
              }
            : undefined,
        minLength: minLength && {
          message: `Please use at least ${minLength} characters.`,
          value: minLength,
        },
        pattern: pattern && {
          message:
            errorMessage || `Please enter a value matching pattern ${pattern}.`,
          value: pattern,
        },
        validate: {
          requiredAllowFalse: (v) => {
            if (requiredAllowFalse) {
              if (v === false) {
                return true;
              } else {
                return !!v || 'Please fill out this field.';
              }
            } else {
              return true;
            }
          },
          required: (v) => {
            if (!required) return true;
            const trimmedValue = typeof v === 'string' ? v.trim() : v;
            return (
              !!trimmedValue ||
              trimmedValue === 0 ||
              requiredMessage ||
              'Please fill out this field.'
            );
          },
          validate: (v) =>
            typeof validate === 'function' ? validate(v) : true,
          minTime: (v) => {
            if (minTime && v) {
              if (minTime > v) {
                return `Please enter a time at or after ${minTime}.`;
              }
            }
            return true;
          },
          maxTime: (v) => {
            if (maxTime && v) {
              if (maxTime < v) {
                return `Please enter a time at or before ${maxTime}.`;
              }
            }
            return true;
          },
        },
      }}
      shouldUnregister={shouldUnregister}
    />
  );
}
